import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { usePageHeader } from '#/providers/page-layout-provider';

export interface PageHeaderPortalProps {
  /** When set (including `null`), syncs to the shell header title; omit to leave title unchanged. */
  title?: string | ReactNode | null;
  /**
   * When `title` is a ReactNode, pass a stable primitive so the layout title effect does not re-run
   * on every render (new element identity would call `setTitle` repeatedly and can exceed max update depth).
   */
  titleSyncKey?: string | number;
  children?: ReactNode;
}

/**
 * Declarative bridge to {@link usePageHeader}: optional `title` for the sticky header title slot,
 * optional `children` portaled into the header actions container.
 */
export function PageHeaderPortal({
  title,
  titleSyncKey,
  children,
}: PageHeaderPortalProps) {
  const { setTitle, actionsRef } = usePageHeader();

  const titleEffectDep = titleSyncKey !== undefined ? titleSyncKey : title;

  useEffect(() => {
    if (title === undefined) return;
    setTitle(title);
    return () => setTitle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `title` is a ReactNode; sync when `titleEffectDep` changes
  }, [setTitle, titleEffectDep]);

  if (children == null || !actionsRef.current) {
    return null;
  }

  return createPortal(children, actionsRef.current);
}
